// Avanti: Empfehlungen "Andere kauften auch" in Schublade und Warenkorbseite.
// Eine Größe: Warenkorb-Icon der Theme-Produktkarten (quick-add).
// Mehrere Größen: Icon öffnet ein eigenes Größenfenster, ein Klick auf eine Größe legt sie direkt in den Warenkorb.
// Außerdem: Pfeile und Zählung der Klicks für die Auswertung.
import { StandardEvents, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const ROUTES = (window.Theme && window.Theme.routes) || { cart_add_url: '/cart/add.js', cart_url: '/cart' };

function sectionIdsToUpdate() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  });
  return Array.from(ids);
}

function track(name, data) {
  try {
    if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
      window.Shopify.analytics.publish(name, data);
    }
  } catch (e) {}
  try {
    if (typeof window.clarity === 'function') {
      window.clarity('event', name);
      window.clarity('set', 'empfehlung_produkt', data.produkt || '');
    }
  } catch (e) {}
}

/* ---------------------------------------------------------------------------
   Größenfenster: Bei Produkten mit mehreren Größen öffnet das Warenkorb-Icon in den
   Empfehlungen (Schublade, Warenkorbseite) nicht das Theme-Fenster (mit Menge und
   PayPal/Shop Pay, das nur dieses eine Produkt bezahlen würde), sondern dieses
   schlanke Fenster: nur die verfügbaren Größen, nichts vorausgewählt, ein Klick
   legt die Größe direkt in den Warenkorb. */
const CLOSE_SVG =
  '<svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><path d="M3.5 3.5l9 9M12.5 3.5l-9 9" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';

let sizeDialog = null;
let sizeDialogContext = 'drawer';

function getSizeDialog() {
  if (sizeDialog && sizeDialog.isConnected) return sizeDialog;
  sizeDialog = document.createElement('dialog');
  sizeDialog.className = 'af-size-dialog';
  sizeDialog.setAttribute('aria-labelledby', 'af-size-dialog-title');
  sizeDialog.innerHTML =
    '<button type="button" class="af-size-dialog__close" aria-label="Schließen" data-af-size-close>' + CLOSE_SVG + '</button>' +
    '<div class="af-size-dialog__head">' +
    '<img class="af-size-dialog__img" alt="" width="56" height="70" hidden>' +
    '<div><p class="af-size-dialog__title" id="af-size-dialog-title"></p>' +
    '<p class="af-size-dialog__hint">Größe wählen, sie kommt direkt in den Warenkorb.</p></div>' +
    '</div>' +
    '<div class="af-size-dialog__options" role="group" aria-label="Größe"></div>' +
    '<p class="af-size-dialog__error" hidden></p>';
  sizeDialog.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    if (target === sizeDialog || target.closest('[data-af-size-close]')) {
      sizeDialog.close();
      return;
    }
    const option = target.closest('[data-af-size-option]');
    if (option instanceof HTMLButtonElement) addSize(option);
  });
  document.body.appendChild(sizeDialog);
  return sizeDialog;
}

function escapeHtml(text) {
  const div = document.createElement('div');
  div.textContent = text == null ? '' : String(text);
  return div.innerHTML;
}

function openSizeDialog(card) {
  const dataEl = card.querySelector('script[data-af-reco-variants]');
  if (!dataEl) return false;
  let data;
  try {
    data = JSON.parse(dataEl.textContent || '{}');
  } catch (e) {
    return false;
  }
  if (!data.variants || !data.variants.length) return false;

  const dialog = getSizeDialog();
  sizeDialogContext = card.closest('.af-reco--page') ? 'page' : 'drawer';
  dialog.dataset.productTitle = data.title || '';
  dialog.querySelector('.af-size-dialog__title').textContent = data.title || '';
  const img = dialog.querySelector('.af-size-dialog__img');
  if (data.image) {
    img.src = data.image;
    img.hidden = false;
  } else {
    img.hidden = true;
  }
  dialog.querySelector('.af-size-dialog__error').hidden = true;
  dialog.querySelector('.af-size-dialog__options').innerHTML = data.variants
    .map(
      (v) =>
        '<button type="button" class="af-size-dialog__option" data-af-size-option data-variant-id="' +
        escapeHtml(v.id) +
        '" aria-label="' +
        escapeHtml((data.title || '') + ', ' + v.title + ', ' + v.price + ', in den Warenkorb legen') +
        '"><span class="af-size-dialog__size">' +
        escapeHtml(v.title) +
        '</span><span class="af-size-dialog__price">' +
        escapeHtml(v.price) +
        '</span></button>'
    )
    .join('');
  if (!dialog.open) dialog.showModal();
  return true;
}

/** Größe aus dem Fenster direkt in den Warenkorb legen */
async function addSize(button) {
  if (button.disabled) return;
  const variantId = button.dataset.variantId;
  if (!variantId) return;
  const dialog = getSizeDialog();
  const buttons = Array.from(dialog.querySelectorAll('[data-af-size-option]'));
  const errorEl = dialog.querySelector('.af-size-dialog__error');
  buttons.forEach((b) => { b.disabled = true; });
  button.setAttribute('aria-busy', 'true');
  errorEl.hidden = true;

  // Ereignis vom Empfehlungsblock aus, damit Schublade/Warenkorbseite und Zähler sich aktualisieren
  const source = document.querySelector('[data-af-reco]') || document.body;
  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart',
      lines: [{ merchandiseId: String(variantId), quantity: 1 }],
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(ROUTES.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1 }],
        sections: sectionIdsToUpdate().join(','),
        sections_url: window.location.pathname,
      }),
    }).then((r) => r.json());

    if (response.status) {
      throw new Error(response.description || response.message || 'Konnte nicht hinzugefügt werden.');
    }

    const cart = await fetch(`${ROUTES.cart_url}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((r) => r.json());

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        itemCount: 1,
        sections: response.sections,
        source: 'af-warenkorb-empfehlungen',
        didError: false,
      },
    });

    track('af_empfehlung_hinzugefuegt', {
      produkt: (dialog.dataset.productTitle || '') + ' ' + (button.querySelector('.af-size-dialog__size')?.textContent || ''),
      ort: sizeDialogContext,
    });

    dialog.close();
  } catch (error) {
    deferred.reject(error);
    errorEl.textContent = (error && error.message) || 'Konnte nicht hinzugefügt werden.';
    errorEl.hidden = false;
    source.dispatchEvent(new CartErrorEvent({ error: (error && error.message) || 'Add to cart failed', code: 'INVALID' }));
  } finally {
    buttons.forEach((b) => { b.disabled = false; });
    button.removeAttribute('aria-busy');
  }
}

/** Pfeile: Zustand (Anfang/Ende/mehr als sichtbar) am Block setzen */
function updateNav(reco) {
  const list = reco && reco.querySelector('.af-reco__list');
  if (!list) return;
  const max = list.scrollWidth - list.clientWidth;
  reco.dataset.overflow = String(max > 4);
  reco.dataset.atStart = String(list.scrollLeft <= 4);
  reco.dataset.atEnd = String(list.scrollLeft >= max - 4);
}

if (!window.__afRecoBound) {
  window.__afRecoBound = true;

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const arrow = target.closest('[data-af-reco-prev], [data-af-reco-next]');
    if (!arrow) return;
    const reco = arrow.closest('[data-af-reco]');
    const list = reco && reco.querySelector('.af-reco__list');
    if (!list) return;
    const firstItem = list.querySelector('.af-reco__item');
    const step = firstItem ? firstItem.getBoundingClientRect().width + 12 : list.clientWidth;
    const pageStep = Math.max(step, Math.floor(list.clientWidth / step) * step);
    list.scrollBy({ left: arrow.hasAttribute('data-af-reco-next') ? pageStep : -pageStep, behavior: 'smooth' });
  });

  // Icon bei Produkten mit mehreren Größen: eigenes Größenfenster statt Theme-Fenster
  window.addEventListener(
    'click',
    (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const button = target ? target.closest('[data-af-reco] .quick-add__button--choose') : null;
      if (!button) return;
      const card = button.closest('product-card');
      if (!card || !card.querySelector('script[data-af-reco-variants]')) return;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      openSizeDialog(card);
    },
    true
  );

  // Hinzufügen aus den Empfehlungen zählen (direkt über das Icon oder über das Auswahlfenster)
  let lastRecoProduct = null;
  document.addEventListener(
    'click',
    (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const card = target ? target.closest('[data-af-reco] product-card') : null;
      if (card && target.closest('.quick-add__button--add')) {
        lastRecoProduct = {
          produkt: card.querySelector('.af-reco__title')?.textContent?.trim() || '',
          ort: card.closest('.af-reco--page') ? 'page' : 'drawer',
          zeit: Date.now(),
        };
      }
    },
    true
  );

  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const fromCard = target ? target.closest('[data-af-reco]') : null;
    const fromModal = target ? target.closest('#quick-add-dialog') : null;
    if (!lastRecoProduct || (!fromCard && !fromModal)) return;
    if (Date.now() - lastRecoProduct.zeit > 120000) return;
    const data = { produkt: lastRecoProduct.produkt, ort: lastRecoProduct.ort };
    lastRecoProduct = null;
    if (event.promise) {
      event.promise.then(({ detail }) => { if (!detail || !detail.didError) track('af_empfehlung_hinzugefuegt', data); }).catch(() => {});
    } else {
      track('af_empfehlung_hinzugefuegt', data);
    }
  });

  document.addEventListener(
    'scroll',
    (event) => {
      const el = event.target;
      if (el instanceof Element && el.classList.contains('af-reco__list')) updateNav(el.closest('[data-af-reco]'));
    },
    true
  );

  document.addEventListener('pointerover', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const reco = target ? target.closest('[data-af-reco]') : null;
    if (reco) updateNav(reco);
  });
}

document.querySelectorAll('[data-af-reco]').forEach(updateNav);
